import { useState, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { useResizeObserver } from '../useResizeObserver';
import { useLayoutEffectWithoutSSRComplaining } from '../useLayoutEffectWithoutSSRComplaining';
function getStyleValue(style, key, defaultValue) {
    if (defaultValue === void 0) { defaultValue = 0; }
    var value = style[key];
    if (value === null || value === undefined) {
        return defaultValue;
    }
    return parseInt(value, 10) || defaultValue;
}
// this measures the _available space_ for our happy little node
// so it can shrink or expand as needed
// note that this requires a non-statically positioned parent
function measureNode(node) {
    var containerNode = node.parentElement;
    if (!containerNode || typeof window === 'undefined') {
        return {};
    }
    var containerWidth = containerNode.offsetWidth || 0;
    var containerHeight = containerNode.offsetHeight || 0;
    var style = window.getComputedStyle(containerNode);
    var paddingLeft = getStyleValue(style, 'paddingLeft');
    var paddingRight = getStyleValue(style, 'paddingRight');
    var paddingTop = getStyleValue(style, 'paddingTop');
    var paddingBottom = getStyleValue(style, 'paddingBottom');
    return {
        contentSize: {
            width: containerWidth - paddingLeft - paddingRight,
            height: containerHeight - paddingTop - paddingBottom,
        },
        containerSize: {
            width: containerWidth,
            height: containerHeight,
        },
    };
}
export function useAvailableSpace(ref, onResize, ssrSizes, throttleTimeout) {
    if (ssrSizes === void 0) { ssrSizes = {}; }
    if (throttleTimeout === void 0) { throttleTimeout = 300; }
    var prevSizes = useRef(ssrSizes); // for equality check without triggering a re-creation of the throttle on every update
    var _a = useState(ssrSizes), sizes = _a[0], setSizes = _a[1]; // for actually triggering a re-render when needed
    var measure = useMemo(function () {
        return throttle(function () {
            if (!ref.current) {
                return;
            }
            var newSizes = measureNode(ref.current);
            if (isEqual(newSizes, prevSizes.current)) {
                return;
            }
            setSizes(newSizes);
            onResize(newSizes);
            prevSizes.current = newSizes;
        }, throttleTimeout, { trailing: true });
    }, [onResize, throttleTimeout, ref]);
    useResizeObserver(ref, measure);
    useLayoutEffectWithoutSSRComplaining(function () {
        measure();
    }, [measure]);
    return sizes;
}
//# sourceMappingURL=useAvailableSpace.js.map